import { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Modal } from './Modal';
import { Button } from './Button';
import { Input } from './Input';
import { RichTextEditor } from './RichTextEditor';
import { emailTemplatesAPI, gmailAPI, mailingListsAPI } from '../../api/endpoints';
import { useBackgroundTask } from '../../hooks/useBackgroundTask';
import { useDefaultTodoDays } from '../../hooks/useDefaultTodoDays';
import { useAuth } from '../../contexts/AuthContext';
import { formatHourRanges, formatInTimezone, resolveUserTimezone, timezoneOffsetLabel } from '../../utils/timezone';
import { toEditableHtml, toStoredHtml } from '../../utils/emailImages';
import { describeExclusions, exclusionTotal } from '../../utils/campaigns';
import type { CampaignSendResponse, EmailTemplate } from '../../types';
import type { SendPeriod } from '../../api/endpoints';
import { FileText, Users, Send, AlertTriangle, CalendarClock, CheckCircle, Loader2 } from 'lucide-react';

interface SendBatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  mailingListId: string;
  listName?: string;
  /** Members that can be emailed, for display — the server resolves the actual recipients */
  recipientCount: number;
  /** The list's last campaign, which the quiet rule measures from (docs/CAMPAIGNS.md) */
  lastCampaignName?: string | null;
  lastCampaignAt?: string | null;
}

const DAYS = [
  { label: 'Mon', value: 0 },
  { label: 'Tue', value: 1 },
  { label: 'Wed', value: 2 },
  { label: 'Thu', value: 3 },
  { label: 'Fri', value: 4 },
  { label: 'Sat', value: 5 },
  { label: 'Sun', value: 6 },
];

// Default allowed hours: 10–12 and 14–16 (business day windows)
const DEFAULT_HOURS = new Set([10, 11, 14, 15]);
const DEFAULT_DAYS = [0, 1, 2, 3, 4];

// Wait for the number being typed to settle before re-running the schedule estimate
const PREVIEW_DEBOUNCE_MS = 400;

// A batch never lays out more than this per rolling 24h, all sends counted: the rest of Gmail's
// 500 is kept for the user's own emails (BATCH_DAILY_CAP on the server, docs/EMAIL_SEND.md)
const BATCH_DAILY_CAP = 400;
const GMAIL_DAILY_CAP = 500;

/** "2 min 24 s" — the spacing a max per day works out to. */
function formatSpacing(seconds: number): string {
  if (seconds < 60) return `${seconds} s`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return m > 0 ? `${h} h ${m} min` : `${h} h`;
  return s > 0 ? `${m} min ${s} s` : `${m} min`;
}

/**
 * Convert the selected hour boxes into a send_period.
 * Box N means "the hour from N:00 to N+1:00", so the range it closes is N+1:00.
 * Every selected hour is sent: a selection with a gap (10-12 and 14-16) stays a gap, and
 * nothing is sent over lunch. hour_start/hour_end only describe the outer span, for older clients.
 * Returns undefined when no hours are selected (= no restriction).
 */
function buildSendPeriod(
  selectedHours: Set<number>,
  daysOfWeek: number[],
  timezone: string,
): SendPeriod | undefined {
  if (selectedHours.size === 0) return undefined;
  const hours = [...selectedHours].sort((a, b) => a - b);
  return {
    days_of_week: daysOfWeek,
    hours,
    hour_start: hours[0],
    hour_end: hours[hours.length - 1] + 1, // max possible: 24
    timezone,
  };
}

/**
 * Send an email to a mailing list as a new campaign, named after its subject (docs/CAMPAIGNS.md).
 * The server works out who receives it: expired, do-not-contact and deleted members are left out.
 * Queuing runs as a background task (as the CSV import does): its progress shows here, and in a
 * toast that keeps going if the modal is closed.
 */
export function SendBatchModal({
  isOpen,
  onClose,
  mailingListId,
  listName,
  recipientCount,
  lastCampaignName,
  lastCampaignAt,
}: SendBatchModalProps) {
  const queryClient = useQueryClient();
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState<CampaignSendResponse | null>(null);
  const [error, setError] = useState('');

  const refreshCampaignViews = () => {
    queryClient.invalidateQueries({ queryKey: ['mailing-lists'] });
    queryClient.invalidateQueries({ queryKey: ['mailing-list-campaigns', mailingListId] });
    queryClient.invalidateQueries({ queryKey: ['inbox'] });
  };

  const { isActive: isQueuing, task, startPolling } = useBackgroundTask({
    label: 'Campaign',
    getSuccessMessage: (t) => {
      const r = t.result as unknown as CampaignSendResponse | null;
      return r
        ? `Campaign "${r.campaign.name}" scheduled: ${r.scheduled_count} email${r.scheduled_count !== 1 ? 's' : ''}`
        : 'Campaign scheduled';
    },
    onComplete: (t) => {
      setResult(t.result as unknown as CampaignSendResponse);
      refreshCampaignViews();
    },
    onError: (t) => {
      setError(t.error || 'The campaign could not be queued.');
    },
  });
  // Reopening the modal mid-send must show the send's progress, not a blank form
  const isQueuingRef = useRef(false);
  isQueuingRef.current = isQueuing;

  // Send period config
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>(DEFAULT_DAYS);
  const [selectedHours, setSelectedHours] = useState<Set<number>>(new Set(DEFAULT_HOURS));
  // Hours are wall-clock times in the user's own timezone, not the server's
  const { user } = useAuth();
  const timezone = resolveUserTimezone(user?.settings?.timezone);

  // Most emails of this campaign per day; the server derives the spacing from it
  const [maxPerDay, setMaxPerDay] = useState(BATCH_DAILY_CAP);

  // One follow-up todo for the whole campaign, written when the campaign is queued and due
  // this many days after its last email goes out (docs/CAMPAIGNS.md). `null` means "whatever
  // Profile -> Settings says", like the contact overlay's single send.
  const defaultTodoDays = useDefaultTodoDays();
  const [todoDaysOverride, setTodoDaysOverride] = useState<number | null>(null);
  const todoDays = todoDaysOverride ?? defaultTodoDays;
  const [disableTodo, setDisableTodo] = useState(false);
  // Reach venues that asked not to be written to yet — a concert invitation, not prospection.
  // Off by default, and the live count below says what ticking it would change (docs/VENUE_PAUSE.md).
  const [includePaused, setIncludePaused] = useState(false);
  // Write only where the venue has gone quiet since the last campaign to this list: a second
  // campaign is a chase, and the venues already in conversation are not its business. On by
  // default; untick it to write to the whole list again (docs/CAMPAIGNS.md).
  const [onlyQuietVenues, setOnlyQuietVenues] = useState(true);
  // Hang each email off the last mail its contact was sent — same thread, same subject, the
  // previous mail quoted below. A recipient with nothing to reply to gets a new email under the
  // subject typed here, which names the campaign either way (docs/EMAIL_SEND.md).
  const [replyToLatest, setReplyToLatest] = useState(false);

  /**
   * Who the send would actually reach, resolved by the server under the same rules the send uses.
   *
   * `recipientCount` is the list's length; this is the eligible count, and the only place the
   * number of paused members — or of venues in touch since the last campaign — can come from.
   * Re-read when either toggle flips, since both change the numbers.
   */
  const { data: recipientsPreview } = useQuery({
    queryKey: ['mailing-list-recipients-preview', mailingListId, includePaused, onlyQuietVenues, replyToLatest],
    queryFn: async ({ signal }) =>
      (await mailingListsAPI.recipientsPreview(mailingListId, includePaused, onlyQuietVenues, replyToLatest, signal))
        .data,
    enabled: isOpen && !isQueuing && !result,
    staleTime: 30000,
  });
  // How many of the list's members only the override would reach.
  const { data: strictPreview } = useQuery({
    queryKey: ['mailing-list-recipients-preview', mailingListId, false, false, false],
    queryFn: async ({ signal }) =>
      (await mailingListsAPI.recipientsPreview(mailingListId, false, false, false, signal)).data,
    enabled: isOpen && !isQueuing && !result,
    staleTime: 30000,
  });
  // The same question with the quiet rule applied, so the count holds whether the box is ticked
  // or not. With it ticked this is the query above, and React Query serves it once — which is why
  // it asks the reply question the same way.
  const { data: quietPreview } = useQuery({
    queryKey: ['mailing-list-recipients-preview', mailingListId, includePaused, true, replyToLatest],
    queryFn: async ({ signal }) =>
      (await mailingListsAPI.recipientsPreview(mailingListId, includePaused, true, replyToLatest, signal)).data,
    enabled: isOpen && !isQueuing && !result,
    staleTime: 30000,
  });
  const pausedCount = strictPreview?.excluded.paused ?? 0;
  const inTouchCount = quietPreview?.excluded.in_touch ?? 0;
  const memberCount = recipientsPreview
    ? recipientsPreview.recipients + exclusionTotal(recipientsPreview.excluded)
    : recipientCount;
  // Who the send will actually reach: the server's own count under the settings on screen. The
  // schedule estimate, the follow-up todos and the button all have to speak about these, not
  // about the list's length — with the quiet rule on, the two are rarely the same number.
  const eligibleCount = recipientsPreview?.recipients ?? recipientCount;
  // How many of them the send can actually reply to. Only resolved while the box is ticked, so
  // `null` means "not asked" rather than "none" (docs/EMAIL_SEND.md).
  const repliableCount = replyToLatest ? recipientsPreview?.repliable ?? null : null;

  const sendPeriod = buildSendPeriod(selectedHours, daysOfWeek, timezone);

  // Estimated last send: the server runs the real layout (window, max per day, the 400/24h cap
  // around what is already queued) without writing anything, so the estimate matches the send.
  const [previewMaxPerDay, setPreviewMaxPerDay] = useState(maxPerDay);
  useEffect(() => {
    const timer = setTimeout(() => setPreviewMaxPerDay(maxPerDay), PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [maxPerDay]);
  const {
    data: preview,
    isFetching: isPreviewing,
    isError: isPreviewError,
  } = useQuery({
    queryKey: ['schedule-preview', eligibleCount, previewMaxPerDay, sendPeriod],
    queryFn: () =>
      gmailAPI
        .schedulePreview({ count: eligibleCount, max_per_day: previewMaxPerDay, send_period: sendPeriod })
        .then((res) => res.data),
    enabled: isOpen && !isQueuing && !result && eligibleCount > 0,
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!isOpen || isQueuingRef.current) return;
    setResult(null);
    setError('');
    setSelectedTemplateId('');
    setSubject('');
    setBody('');
    setDaysOfWeek(DEFAULT_DAYS);
    setSelectedHours(new Set(DEFAULT_HOURS));
    setTodoDaysOverride(null);
    setDisableTodo(false);
    setIncludePaused(false);
    setOnlyQuietVenues(true);
    setReplyToLatest(false);
    emailTemplatesAPI
      .list()
      .then((res) => setEmailTemplates(res.data.items))
      .catch(() => setEmailTemplates([]));
  }, [isOpen]);

  const handleApplyTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (!templateId) return;
    const template = emailTemplates.find((t) => t._id === templateId);
    if (template) {
      setSubject(template.subject);
      setBody(toEditableHtml(template.email_text));
    }
  };

  const toggleDay = (day: number) => {
    setDaysOfWeek((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort((a, b) => a - b)
    );
  };

  const toggleHour = (hour: number) => {
    setSelectedHours((prev) => {
      const next = new Set(prev);
      if (next.has(hour)) next.delete(hour);
      else next.add(hour);
      return next;
    });
  };

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      setError('Subject and body are required.');
      return;
    }
    if (eligibleCount === 0) {
      setError(
        onlyQuietVenues && inTouchCount > 0
          ? 'Nobody left to write to: every venue on this list has been in touch since the last campaign.'
          : 'No recipients to send to.'
      );
      return;
    }

    setError('');
    setIsSending(true);
    try {
      const res = await mailingListsAPI.sendCampaign(mailingListId, {
        subject: subject.trim(),
        // The editor holds blob URLs; the campaign stores cid: references (docs/EMAIL_IMAGES.md)
        body: toStoredHtml(body),
        max_per_day: maxPerDay,
        send_period: sendPeriod,
        create_todo: !disableTodo,
        todo_days: todoDays,
        include_paused: includePaused,
        only_quiet_venues: onlyQuietVenues,
        reply_to_latest: replyToLatest,
      });
      startPolling(res.data.task_id);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
          ? detail.map((d: { msg?: string }) => d.msg ?? JSON.stringify(d)).join('; ')
          : 'Failed to send batch emails. Please try again.';
      setError(msg);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Send Batch Email" size="4xl">
      {isQueuing ? (
        // ── Queuing state ──────────────────────────────────────────────
        <div className="p-6">
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-12 h-12 text-dark-accent animate-spin mb-4" />
            <p className="text-lg text-dark-text-primary mb-2">Scheduling campaign...</p>
            <p className="text-dark-text-secondary">
              {task?.message || `Queuing ${eligibleCount} email${eligibleCount !== 1 ? 's' : ''}`}
            </p>
            {task && task.progress > 0 && (
              <div className="w-64 mt-4 bg-dark-elevated rounded-full h-2">
                <div
                  className="bg-dark-accent h-2 rounded-full transition-all"
                  style={{ width: `${Math.round(task.progress)}%` }}
                />
              </div>
            )}
            <p className="mt-6 text-xs text-dark-text-muted text-center max-w-md">
              Nothing is sent until every email is queued. You can close this window: queuing
              continues and a notification tells you when it is done.
            </p>
          </div>
          <div className="flex justify-end">
            <Button variant="ghost" onClick={onClose}>Close</Button>
          </div>
        </div>
      ) : result ? (
        // ── Success state ──────────────────────────────────────────────
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-8 h-8 text-green-400 shrink-0 mt-0.5" />
            <div>
              <h3 className="text-base font-semibold text-dark-text-primary">
                Campaign scheduled
              </h3>
              <p className="text-sm text-dark-text-primary mt-1 font-medium">"{result.campaign.name}"</p>
              <p className="text-sm text-dark-text-secondary mt-1">
                {result.scheduled_count} email{result.scheduled_count !== 1 ? 's' : ''} scheduled
                {result.days_spanned > 1 && ` over ${result.days_spanned} days`}.
              </p>
              {exclusionTotal(result.campaign.excluded) > 0 && (
                <p className="text-xs text-dark-text-secondary mt-1">
                  Not sent to: {describeExclusions(result.campaign.excluded)}
                </p>
              )}
              {result.scheduled_count > 0 && (
                <div className="mt-2 text-xs text-dark-text-muted space-y-0.5">
                  <div>First: {formatInTimezone(result.first_scheduled_at, timezone)}</div>
                  <div>Last: {formatInTimezone(result.last_scheduled_at, timezone)}</div>
                  <div>Times shown in {timezone}</div>
                  <div>
                    {disableTodo
                      ? 'No follow-up todo'
                      : `One follow-up todo for this campaign, ${todoDays} day${todoDays !== 1 ? 's' : ''} after the last send`}
                  </div>
                  {replyToLatest && (
                    <div>
                      {result.replied_count ?? 0} as a reply to the contact's last email
                      {(result.replied_count ?? 0) < result.scheduled_count &&
                        `, ${result.scheduled_count - (result.replied_count ?? 0)} as a new email`}
                    </div>
                  )}
                </div>
              )}
              {result.deferred_by_cap_count > 0 && (
                <div className="mt-2 flex items-start gap-1.5 text-xs text-dark-text-secondary">
                  <CalendarClock className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>
                    At most {maxPerDay} a day, and never more than {BATCH_DAILY_CAP} a day across all your
                    sends, so {result.deferred_by_cap_count} of them go out on later days. Nothing was dropped.
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end">
            <Button variant="primary" onClick={onClose}>Done</Button>
          </div>
        </div>
      ) : (
        // ── Compose state ─────────────────────────────────────────────
        <div className="p-5 space-y-4 max-h-[85vh] overflow-y-auto">
          {/* Recipients summary */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-dark-elevated border border-dark-border text-sm">
            <Users className="w-4 h-4 text-dark-text-muted shrink-0" />
            <span className="text-dark-text-secondary">
              Sending to{' '}
              <span className="font-semibold text-dark-text-primary">{eligibleCount}</span>
              {' '}contact{eligibleCount !== 1 ? 's' : ''}
              {memberCount > eligibleCount && <> of {memberCount}</>}
              {listName && <> in <span className="font-medium text-dark-text-primary">"{listName}"</span></>}
              <span className="text-dark-text-muted">
                {' '}· expired and do-not-contact contacts are never emailed, even if flagged after sending starts
              </span>
            </span>
          </div>

          {/* Paused venues: the override, and what it would change (docs/VENUE_PAUSE.md) */}
          {(pausedCount > 0 || includePaused) && (
            <label className="flex items-start gap-2 px-3 py-2 rounded-lg bg-orange-900/10 border border-orange-700/40 cursor-pointer">
              <input
                type="checkbox"
                checked={includePaused}
                onChange={(e) => setIncludePaused(e.target.checked)}
                className="w-3 h-3 mt-1 rounded-sm border-dark-border bg-dark-elevated"
              />
              <span className="text-sm text-dark-text-secondary">
                Send to paused venues too
                <span className="text-dark-text-muted"> — for a concert invitation, not for prospection</span>
                <span className="block text-xs text-orange-400/90 mt-0.5">
                  {pausedCount} of {memberCount} member{pausedCount !== 1 ? 's' : ''} asked not to be written to yet
                  {includePaused ? ' — they will be emailed.' : ' — they are left out.'}
                </span>
              </span>
            </label>
          )}

          {/* The quiet rule: skip the venues already in conversation (docs/CAMPAIGNS.md) */}
          <label className="flex items-start gap-2 px-3 py-2 rounded-lg bg-dark-elevated border border-dark-border cursor-pointer">
            <input
              type="checkbox"
              checked={onlyQuietVenues}
              onChange={(e) => setOnlyQuietVenues(e.target.checked)}
              className="w-3 h-3 mt-1 rounded-sm border-dark-border bg-dark-elevated"
            />
            <span className="text-sm text-dark-text-secondary">
              Only venues that have gone quiet since the last campaign
              <span className="text-dark-text-muted"> — skip the ones you are already talking to</span>
              {lastCampaignAt ? (
                <span className="block text-xs mt-0.5">
                  <span className="text-dark-text-muted">
                    Nothing sent, received or logged at the venue since{' '}
                    {lastCampaignName ? `"${lastCampaignName}"` : 'the last campaign'} (
                    {format(new Date(lastCampaignAt), 'dd MMM yyyy')}).{' '}
                  </span>
                  <span className={onlyQuietVenues ? 'text-primary-400' : 'text-dark-text-muted'}>
                    {inTouchCount} member{inTouchCount !== 1 ? 's' : ''} ha{inTouchCount !== 1 ? 've' : 's'} been
                    in touch since then
                    {onlyQuietVenues ? ' — they are left out.' : ' — they will be emailed too.'}
                  </span>
                </span>
              ) : (
                <span className="block text-xs text-dark-text-muted mt-0.5">
                  No campaign has gone to this list yet, so this leaves nobody out.
                </span>
              )}
            </span>
          </label>

          {/* Reply rather than a fresh email: same thread, same subject (docs/EMAIL_SEND.md) */}
          <label className="flex items-start gap-2 px-3 py-2 rounded-lg bg-dark-elevated border border-dark-border cursor-pointer">
            <input
              type="checkbox"
              checked={replyToLatest}
              onChange={(e) => setReplyToLatest(e.target.checked)}
              className="w-3 h-3 mt-1 rounded-sm border-dark-border bg-dark-elevated"
            />
            <span className="text-sm text-dark-text-secondary">
              Send as a reply to the last email you sent
              <span className="text-dark-text-muted">
                {' '}— in the same thread, under its subject, with that email quoted below
              </span>
              {replyToLatest ? (
                repliableCount === null ? (
                  <span className="block text-xs text-dark-text-muted mt-0.5">
                    Looking up who has an email to reply to...
                  </span>
                ) : (
                  <span className="block text-xs mt-0.5">
                    <span className="text-primary-400">
                      {repliableCount} of {eligibleCount} recipient{eligibleCount !== 1 ? 's' : ''} ha
                      {repliableCount !== 1 ? 've' : 's'} an email to reply to
                    </span>
                    <span className="text-dark-text-muted">
                      {eligibleCount - repliableCount > 0
                        ? ` — the other ${eligibleCount - repliableCount} get a new email under the subject below.`
                        : '.'}
                    </span>
                  </span>
                )
              ) : (
                <span className="block text-xs text-dark-text-muted mt-0.5">
                  Every recipient gets a new email under the subject below.
                </span>
              )}
            </span>
          </label>

          {/* Template selector */}
          {emailTemplates.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-dark-text-secondary mb-1">
                <span className="flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  Template
                </span>
              </label>
              <select
                value={selectedTemplateId}
                onChange={(e) => handleApplyTemplate(e.target.value)}
                className="w-full px-3 py-2 bg-dark-elevated border border-dark-border rounded-lg text-dark-text-primary text-sm focus:outline-hidden focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select a template...</option>
                {emailTemplates.map((t) => (
                  <option key={t._id} value={t._id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Subject */}
          <div>
            <Input
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject..."
              required
            />
            <p className="mt-1 text-xs text-dark-text-muted">
              The subject is also the campaign's name. Each send creates a new campaign.
              {replyToLatest &&
                ' A reply keeps the subject of the thread it joins, so this one only reaches the recipients' +
                  ' with nothing to reply to.'}
            </p>
          </div>

          {/* Body */}
          <div>
            <label className="block text-xs font-medium text-dark-text-secondary mb-1">Message</label>
            <RichTextEditor content={body} onChange={setBody} placeholder="Compose your email..." />
          </div>

          {/* ── Send window ──────────────────────────────────────── */}
          <div className="space-y-3 pt-3 border-t border-dark-border">
            <div className="text-xs font-semibold text-dark-text-secondary uppercase tracking-wide">
              Send Window
            </div>

            {/* Days of week */}
            <div>
              <label className="block text-xs font-medium text-dark-text-secondary mb-1.5">
                Days{' '}
                <span className="text-dark-text-muted font-normal">(empty = any day)</span>
              </label>
              <div className="flex gap-1.5">
                {DAYS.map((d) => (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => toggleDay(d.value)}
                    className={`flex-1 py-1.5 rounded text-xs font-medium transition-all border ${
                      daysOfWeek.includes(d.value)
                        ? 'bg-primary-900/30 text-primary-400 border-primary-700/50'
                        : 'bg-dark-elevated text-dark-text-secondary border-dark-border hover:bg-dark-border'
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Hours grid — 24 boxes */}
            <div>
              <label className="block text-xs font-medium text-dark-text-secondary mb-1.5">
                Hours{' '}
                <span className="text-dark-text-muted font-normal">(empty = any hour)</span>
              </label>
              <div className="grid grid-cols-12 gap-1">
                {Array.from({ length: 24 }, (_, h) => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => toggleHour(h)}
                    title={`${String(h).padStart(2, '0')}:00 – ${String(h + 1).padStart(2, '0')}:00`}
                    className={`py-1.5 rounded text-xs font-medium transition-all border ${
                      selectedHours.has(h)
                        ? 'bg-primary-900/30 text-primary-400 border-primary-700/50'
                        : 'bg-dark-elevated text-dark-text-secondary border-dark-border hover:bg-dark-border'
                    }`}
                  >
                    {h}
                  </button>
                ))}
              </div>
              <div className="mt-1 text-xs text-dark-text-muted">
                {timezone}
                {timezoneOffsetLabel(timezone) && ` (${timezoneOffsetLabel(timezone)})`}
                {selectedHours.size > 0 && ` · ${formatHourRanges([...selectedHours])}`}
              </div>
              {!user?.settings?.timezone && (
                <div className="mt-1 text-xs text-dark-text-muted">
                  Using this browser's timezone. Set yours in Profile → Settings.
                </div>
              )}
            </div>

            {/* Max per day: the server spaces the emails so this many fill a day's sending hours */}
            <div className="flex items-center gap-3">
              <div className="w-44">
                <label className="block text-xs font-medium text-dark-text-secondary mb-1">
                  Max emails per day
                </label>
                <Input
                  type="number"
                  value={maxPerDay}
                  onChange={(e) =>
                    setMaxPerDay(Math.min(BATCH_DAILY_CAP, Math.max(1, Math.round(Number(e.target.value)) || 1)))
                  }
                  min={1}
                  max={BATCH_DAILY_CAP}
                />
              </div>
              <div className="text-xs mt-5 space-y-0.5">
                <p className="text-dark-text-muted">
                  {preview
                    ? `≈ one email every ${formatSpacing(preview.interval_seconds)} during your sending hours. `
                    : ''}
                  Never more than {BATCH_DAILY_CAP} a day across all your sends: the other{' '}
                  {GMAIL_DAILY_CAP - BATCH_DAILY_CAP} of Gmail's {GMAIL_DAILY_CAP} are kept for your own emails.
                </p>
                {eligibleCount > 0 && (
                  <p
                    className={`flex items-center gap-1.5 text-dark-text-secondary transition-opacity ${
                      isPreviewing ? 'opacity-60' : ''
                    }`}
                  >
                    <CalendarClock className="w-3.5 h-3.5 shrink-0" />
                    {preview ? (
                      <span>
                        Last email ≈{' '}
                        <span className="font-medium text-dark-text-primary">
                          {formatInTimezone(preview.last_scheduled_at, timezone)}
                        </span>{' '}
                        if sent now
                        {preview.days_spanned > 1 && ` · spread over ${preview.days_spanned} days`}
                      </span>
                    ) : isPreviewError ? (
                      'Estimate unavailable'
                    ) : (
                      'Estimating last send…'
                    )}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* ── Follow-up todo ───────────────────────────────────── */}
          <div className="space-y-2 pt-3 border-t border-dark-border">
            <div className="text-xs font-semibold text-dark-text-secondary uppercase tracking-wide">
              Follow-up
            </div>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                label="Todo (days)"
                value={todoDays}
                onChange={(e) => setTodoDaysOverride(Math.max(1, Number(e.target.value)))}
                className={`w-24 ${disableTodo ? 'opacity-50' : ''}`}
                min={1}
                disabled={disableTodo}
              />
              <label className="flex items-center gap-2 cursor-pointer mt-5">
                <input
                  type="checkbox"
                  checked={disableTodo}
                  onChange={(e) => setDisableTodo(e.target.checked)}
                  className="w-3 h-3 rounded-sm border-dark-border bg-dark-elevated"
                />
                <span className="text-xs text-dark-text-secondary">Disable todo</span>
              </label>
            </div>
            <p className="text-xs text-dark-text-muted">
              {disableTodo
                ? 'No follow-up todo is created for this campaign.'
                : `One follow-up todo for the whole campaign — not one per recipient — created now, linked to this list, and due ${todoDays} day${todoDays !== 1 ? 's' : ''} after its last email goes out.`}
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-900/20 border border-red-700/40 text-red-400 text-sm">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2 border-t border-dark-border">
            <Button variant="ghost" onClick={onClose} disabled={isSending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSend}
              isLoading={isSending}
              disabled={isSending || !subject.trim() || !body.trim() || eligibleCount === 0}
            >
              <Send className="w-4 h-4" />
              Send to {eligibleCount} contact{eligibleCount !== 1 ? 's' : ''}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
